import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Archive,
  Copy,
  MoreHorizontal,
  PauseCircle,
  Pencil,
  PlayCircle,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { AgentStatus } from "@/lib/agentsDemoData";

export type AgentMenuSettingsItem = {
  id: string;
  label: string;
  icon: LucideIcon;
  onSelect: () => void;
};

/** @deprecated Use AgentMenuSettingsItem. */
export type AgentMenuConfigItem = AgentMenuSettingsItem;

type AgentActionsMenuProps = {
  status: AgentStatus;
  disabled?: boolean;
  align?: "left" | "right";
  settingsItems?: AgentMenuSettingsItem[];
  /** @deprecated Prefer settingsItems. */
  configItems?: AgentMenuSettingsItem[];
  onEdit: () => void;
  onDuplicate: () => void;
  onActivate: () => void;
  onDeactivate: () => void;
  onArchive: () => void;
};

type MenuCoords = {
  top: number;
  left: number;
  openAbove: boolean;
};

const MENU_WIDTH = 224; // w-56
const MENU_GAP = 4;
const VIEWPORT_PAD = 8;

function computeMenuCoords(
  trigger: DOMRect,
  menuHeight: number,
  align: "left" | "right",
): MenuCoords {
  const spaceBelow = window.innerHeight - trigger.bottom - VIEWPORT_PAD;
  const spaceAbove = trigger.top - VIEWPORT_PAD;
  const openAbove = spaceBelow < menuHeight && spaceAbove > spaceBelow;

  let left = align === "left" ? trigger.left : trigger.right - MENU_WIDTH;
  left = Math.min(
    Math.max(VIEWPORT_PAD, left),
    window.innerWidth - MENU_WIDTH - VIEWPORT_PAD,
  );

  const top = openAbove
    ? Math.max(VIEWPORT_PAD, trigger.top - MENU_GAP - menuHeight)
    : trigger.bottom + MENU_GAP;

  return { top, left, openAbove };
}

export function AgentActionsMenu({
  status,
  disabled = false,
  align = "right",
  settingsItems,
  configItems = [],
  onEdit,
  onDuplicate,
  onActivate,
  onDeactivate,
  onArchive,
}: AgentActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<MenuCoords | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const archived = status === "archived";
  const inactive = status === "paused";
  const menuSettings = settingsItems ?? configItems;

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight ?? 280;
    setCoords(computeMenuCoords(rect, menuHeight, align));
  }, [align]);

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    updatePosition();
  }, [open, updatePosition, menuSettings.length, status]);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    function onReposition() {
      updatePosition();
    }

    window.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open, updatePosition]);

  const run = (action: () => void) => {
    setOpen(false);
    action();
  };

  const itemClass =
    "flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 disabled:opacity-40 dark:text-zinc-100 dark:hover:bg-white/5";

  const menu = open ? (
    <div
      ref={menuRef}
      role="menu"
      style={
        coords
          ? { top: coords.top, left: coords.left }
          : { top: 0, left: 0, visibility: "hidden" as const }
      }
      className={cn(
        "fixed z-[200] max-h-[min(28rem,70vh)] w-56 overflow-y-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg dark:border-white/10 dark:bg-zinc-900",
        coords?.openAbove ? "origin-bottom" : "origin-top",
      )}
    >
      <button
        type="button"
        role="menuitem"
        disabled={archived}
        className={itemClass}
        onClick={() => run(onEdit)}
      >
        <Pencil className="h-4 w-4 shrink-0" aria-hidden />
        Edit
      </button>
      <button type="button" role="menuitem" className={itemClass} onClick={() => run(onDuplicate)}>
        <Copy className="h-4 w-4 shrink-0" aria-hidden />
        Duplicate
      </button>
      {!archived && inactive ? (
        <button
          type="button"
          role="menuitem"
          className={itemClass}
          onClick={() => run(onActivate)}
        >
          <PlayCircle className="h-4 w-4 shrink-0" aria-hidden />
          Activate
        </button>
      ) : null}
      {!archived && !inactive ? (
        <button
          type="button"
          role="menuitem"
          className={itemClass}
          onClick={() => run(onDeactivate)}
        >
          <PauseCircle className="h-4 w-4 shrink-0" aria-hidden />
          Deactivate
        </button>
      ) : null}

      {menuSettings.length > 0 ? (
        <>
          <div
            className="my-1 border-t border-slate-200 dark:border-white/10"
            role="separator"
          />
          <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            Settings
          </p>
          {menuSettings.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                className={itemClass}
                onClick={() => run(item.onSelect)}
              >
                <Icon className="h-4 w-4 shrink-0" aria-hidden />
                {item.label}
              </button>
            );
          })}
        </>
      ) : null}

      {!archived ? (
        <>
          <div
            className="my-1 border-t border-slate-200 dark:border-white/10"
            role="separator"
          />
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-950/40"
            onClick={() => run(onArchive)}
          >
            <Archive className="h-4 w-4 shrink-0" aria-hidden />
            Archive
          </button>
        </>
      ) : null}
    </div>
  ) : null;

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
        className="inline-flex items-center justify-center rounded-md border border-slate-300 p-2 text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-white/20 dark:text-zinc-200 dark:hover:bg-white/5"
        aria-label="Agent actions"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden />
      </button>
      {menu && typeof document !== "undefined"
        ? createPortal(menu, document.body)
        : null}
    </div>
  );
}
